// The console's read model: the activity → goal → plan → task tree plus the
// metrics hanging off it, assembled from the sync collections. The entities
// reference *upwards* on the wire (a goal knows its activityId, a task its planId
// — never the reverse), so the tree is rebuilt here by grouping, and the
// components below just render it.

import { useMemo, useSyncExternalStore } from "react";
import type {
  ActivityInput,
  LongTermGoalInput,
  MetricInput,
  PlanInput,
  PointInput,
  TaskInput,
} from "@/types";
import type { EntityRecord } from "@/sync/entityCollection";
import {
  activityStore,
  goalStore,
  metricStore,
  planStore,
  pointStore,
  taskStore,
} from "@/services/client";

export type ActivityRecord = EntityRecord<ActivityInput>;
export type GoalRecord = EntityRecord<LongTermGoalInput>;
export type PlanRecord = EntityRecord<PlanInput>;
export type TaskRecord = EntityRecord<TaskInput>;
export type MetricRecord = EntityRecord<MetricInput>;
export type PointRecord = EntityRecord<PointInput>;

/** A metric with its readings, oldest first. */
export type MetricView = { metric: MetricRecord; points: PointRecord[] };
/** A plan with its steps, in sortKey order. */
export type PlanView = { plan: PlanRecord; tasks: TaskRecord[] };
export type GoalView = { goal: GoalRecord; plans: PlanView[]; metrics: MetricView[] };
export type ActivityView = {
  activity: ActivityRecord;
  goals: GoalView[];
  metrics: MetricView[];
};

export function useDashboard(): ActivityView[] {
  const activities = useSyncExternalStore(activityStore.subscribe, activityStore.getSnapshot);
  const goals = useSyncExternalStore(goalStore.subscribe, goalStore.getSnapshot);
  const plans = useSyncExternalStore(planStore.subscribe, planStore.getSnapshot);
  const tasks = useSyncExternalStore(taskStore.subscribe, taskStore.getSnapshot);
  const metrics = useSyncExternalStore(metricStore.subscribe, metricStore.getSnapshot);
  const points = useSyncExternalStore(pointStore.subscribe, pointStore.getSnapshot);

  return useMemo(() => {
    const pointsByMetric = groupBy(points, (point) => point.fields.metricId);
    for (const series of pointsByMetric.values()) {
      series.sort((a, b) => (a.fields.at < b.fields.at ? -1 : 1));
    }
    const withPoints = (metric: MetricRecord): MetricView => ({
      metric,
      points: pointsByMetric.get(metric.id) ?? [],
    });

    // A metric attaches to exactly one owner: an activity or a goal, never both.
    const metricsByActivity = groupBy(metrics, (metric) => metric.fields.activityId);
    const metricsByGoal = groupBy(metrics, (metric) => metric.fields.ltgId);

    const tasksByPlan = groupBy(tasks, (task) => task.fields.planId);
    for (const steps of tasksByPlan.values()) {
      steps.sort((a, b) => (a.fields.sortKey < b.fields.sortKey ? -1 : 1));
    }
    const plansByGoal = groupBy(plans, (plan) => plan.fields.ltgId);
    const goalsByActivity = groupBy(goals, (goal) => goal.fields.activityId);

    return activities.map((activity) => ({
      activity,
      metrics: (metricsByActivity.get(activity.id) ?? []).map(withPoints),
      goals: (goalsByActivity.get(activity.id) ?? []).map((goal) => ({
        goal,
        metrics: (metricsByGoal.get(goal.id) ?? []).map(withPoints),
        plans: (plansByGoal.get(goal.id) ?? []).map((plan) => ({
          plan,
          tasks: tasksByPlan.get(plan.id) ?? [],
        })),
      })),
    }));
  }, [activities, goals, plans, tasks, metrics, points]);
}

/** Bucket records by a parent id, skipping those with no parent (e.g. a metric
 * owned by a goal rather than an activity). */
function groupBy<T>(items: T[], parentId: (item: T) => string | undefined): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const key = parentId(item);
    if (key === undefined) continue;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(item);
    else grouped.set(key, [item]);
  }
  return grouped;
}
